import maplibregl, { type Map as MLMap, type LngLatLike, type Marker } from "maplibre-gl";
import type { Feature, FeatureCollection, Polygon } from "geojson";
import "maplibre-gl/dist/maplibre-gl.css";
import { layers, namedFlavor } from "@protomaps/basemaps";
import type { BBox } from "../../shared/game.ts";

const TILES = import.meta.env.VITE_TILES_URL || "https://tiles.playgeopin.com/world.json";
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;

/** GeoPin's map look: warm land, soft water, clear borders, and no place names. */
export function style(): maplibregl.StyleSpecification {
  const f = structuredClone(namedFlavor("light")) as any;
  Object.assign(f, {
    background: "#cfe3ee", earth: "#f6f1e7", water: "#b9d8ea", park_a: "#e3ecd2", park_b: "#d7e6c2",
    wood_a: "#e6ecd6", wood_b: "#dde7cb", scrub_a: "#ebe9d6", scrub_b: "#e4e3cc", glacier: "#ffffff",
    sand: "#efe6cf", beach: "#f1e6c8", boundaries: "#8d8170", major: "#ffffff", highway: "#f6d9a6",
    minor_a: "#ffffff", minor_b: "#ffffff", buildings: "#e8e1d3",
  });
  const ls = layers("protomaps", f, { lang: "en" }).filter((l: any) => l.type !== "symbol") as any[];
  for (const l of ls) {
    if (l.id === "boundaries_country") {
      l.paint["line-width"] = ["interpolate", ["linear"], ["zoom"], 1, 0.8, 5, 1.8, 10, 3];
      delete l.paint["line-dasharray"];
    }
    if (l.id === "boundaries") {
      // states/provinces only (kind_detail 4); finer admin units add clutter, not help
      l.filter = l.filter ? ["all", l.filter, ["<=", "kind_detail", 4]] : ["<=", "kind_detail", 4];
      l.paint["line-width"] = ["interpolate", ["linear"], ["zoom"], 3, 0.5, 6, 1.2, 10, 2];
    }
  }
  return {
    version: 8,
    sources: { protomaps: { type: "vector", url: TILES, attribution: '<a href="https://protomaps.com">Protomaps</a> © <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>' } },
    layers: ls,
  };
}

let map: MLMap;
let spinTimer = 0;

export function createMap(container: HTMLElement): MLMap {
  map = new maplibregl.Map({
    container, style: style(), center: [-30, 25], zoom: 1.6, minZoom: 0.6, maxZoom: 14,
    attributionControl: { compact: true }, dragRotate: false, pitchWithRotate: false, touchPitch: false,
  });
  map.touchZoomRotate.disableRotation();
  map.on("style.load", () => {
    map.setProjection({ type: "globe" });
    map.addSource("overlay", { type: "geojson", data: emptyFC() });
    map.addLayer({ id: "ring-fill", type: "fill", source: "overlay", filter: ["==", ["get", "kind"], "ring"],
      paint: { "fill-color": ["get", "color"], "fill-opacity": 0.08 } });
    map.addLayer({ id: "ring-line", type: "line", source: "overlay", filter: ["==", ["get", "kind"], "ring"],
      paint: { "line-color": ["get", "color"], "line-width": 2, "line-dasharray": [2, 1.5] } });
    map.addLayer({ id: "link", type: "line", source: "overlay", filter: ["==", ["get", "kind"], "link"],
      paint: { "line-color": "#0B1B33", "line-width": 2.5, "line-dasharray": [2, 1.2] } });
  });
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");
  return map;
}

export const getMap = () => map;

export function spinGlobe(on: boolean) {
  cancelAnimationFrame(spinTimer);
  if (!on) map.setPadding({ left: 0, right: 0, top: 0, bottom: 0 });
  if (!on || reducedMotion) return;
  const step = () => {
    if (!map.isMoving()) map.setCenter([map.getCenter().lng + 0.03, map.getCenter().lat]);
    spinTimer = requestAnimationFrame(step);
  };
  spinTimer = requestAnimationFrame(step);
}

export function fitBBox(b: BBox, opts: { padding?: number | maplibregl.PaddingOptions; animate?: boolean; maxZoom?: number } = {}) {
  map.fitBounds([[b[0], b[1]], [b[2], b[3]]], {
    padding: opts.padding ?? padFor(), animate: opts.animate !== false && !reducedMotion && !document.hidden, duration: 1200,
    maxZoom: opts.maxZoom ?? 9,
  });
}

export function fitPoints(points: [number, number][], maxZoom = 8) {
  if (!points.length) return;
  const b = new maplibregl.LngLatBounds(points[0], points[0]);
  points.forEach(p => b.extend(p));
  map.fitBounds(b, { padding: padFor(), maxZoom, animate: !reducedMotion && !document.hidden, duration: 1000 });
}

function padFor() {
  const w = map.getContainer().clientWidth;
  return w < 640 ? { top: 170, bottom: 260, left: 30, right: 30 } as any : { top: 150, bottom: 120, left: 80, right: 300 } as any;
}

// ---------------------------------------------------------------- markers

export type PinColor = "guess" | "answer" | string;

export function pinEl(color: PinColor, label?: string, check = false): HTMLElement {
  const fill = color === "guess" ? "#FF7A59" : color === "answer" ? "#8BD43A" : color;
  const wrap = document.createElement("div");
  wrap.className = "pin";
  const inner = check
    ? '<path d="M10.5 16l3.5 3.5 7-7" stroke="#0B1B33" stroke-width="2.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/>'
    : '<circle cx="16" cy="16" r="6" fill="#ffffff"/>';
  wrap.innerHTML = `<svg aria-hidden="true" width="32" height="44" viewBox="0 0 32 44"><path d="M16 0C7.2 0 0 7.2 0 16c0 11.2 16 28 16 28s16-16.8 16-28C32 7.2 24.8 0 16 0z" fill="${fill}" stroke="#0B1B33" stroke-width="1.5"/>${inner}</svg>`;
  if (label) {
    const tag = document.createElement("span");
    tag.className = "pin-label";
    tag.textContent = label;
    wrap.append(tag);
  }
  return wrap;
}

const markers: Marker[] = [];
export function addPin(lngLat: LngLatLike, color: PinColor, label?: string, check = false): Marker {
  const m = new maplibregl.Marker({ element: pinEl(color, label, check), anchor: "bottom" }).setLngLat(lngLat).addTo(map);
  markers.push(m);
  return m;
}
export function clearPins() { markers.splice(0).forEach(m => m.remove()); }

// ---------------------------------------------------------------- rings & lines

const RING_COLORS = ["#E0A800", "#5DA92A", "#2B8FD0", "#7F95B8"];

export function showRings(center: [number, number], radiiKm: number[], link?: [number, number][]) {
  const features: Feature[] = radiiKm.slice().reverse().map((km, i, arr) => ({
    type: "Feature", properties: { kind: "ring", color: RING_COLORS[arr.length - 1 - i] }, geometry: circle(center, km),
  }));
  if (link) for (const from of link) features.push({ type: "Feature", properties: { kind: "link" }, geometry: { type: "LineString", coordinates: [from, center] } });
  (map.getSource("overlay") as maplibregl.GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features });
}

export function clearOverlay() { (map.getSource("overlay") as maplibregl.GeoJSONSource | undefined)?.setData(emptyFC()); }

function emptyFC(): FeatureCollection { return { type: "FeatureCollection", features: [] }; }

function circle([lng, lat]: [number, number], km: number, steps = 96): Polygon {
  const R = 6371.0088, d = km / R, la = (lat * Math.PI) / 180, lo = (lng * Math.PI) / 180;
  const ring: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const b = (i / steps) * 2 * Math.PI;
    const la2 = Math.asin(Math.sin(la) * Math.cos(d) + Math.cos(la) * Math.sin(d) * Math.cos(b));
    const lo2 = lo + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(la), Math.cos(d) - Math.sin(la) * Math.sin(la2));
    ring.push([(lo2 * 180) / Math.PI, (la2 * 180) / Math.PI]);
  }
  return { type: "Polygon", coordinates: [ring] };
}
